/**
 * API hooks for Universes and related data
 * Provides access to universe, benchmark, and stock data
 */

import { useMemo } from "react";
import { useApiQuery } from "@repo/api";
import type { ApiRequestConfig } from "@repo/api";
import { useTranslation } from "@repo/core";
import type { QuickFilter } from "../../types/factor-mimicking.ts";

// Universe interface based on DynamoDB structure
export interface Universe {
  // Primary keys
  PK: string; // "UNIVERSES"
  SK: string; // universe identifier (e.g., "ftse100")

  // Core properties
  universe: string; // Display name (e.g., "FTSE 100")
  entityType: "UNIVERSE";
  assetsCount: number; // Total number of assets in the universe

  // Quantile configuration
  numberOfQuantiles: number; // Number of quantiles for ranking
  topAssets: number; // Number of top-performing assets to consider
  bottomAssets: number; // Number of bottom-performing assets to consider

  // Long/Short mapping for quantiles
  longShortQuantiles: {
    [quantile: string]: "long" | "short"; // e.g., { "Q1": "short", "Q5": "long" }
  };

  // Symbol suffixes for different data providers
  suffix: {
    [provider: string]: string; // e.g., { "eod": ".LSE", "yfinance": ".L" }
  };
}

// Simplified Universe interface for API responses (flattened structure)
export interface UniverseResponse {
  universeId: string; // Derived from SK
  name: string; // Derived from universe field
  assetsCount: number;
  numberOfQuantiles: number;
  topAssets: number;
  bottomAssets: number;
  modelsKey: string;
  showOnFE: boolean | undefined;
  universe: string;
  longShortQuantiles: {
    [quantile: string]: "long" | "short";
  };
  suffix: {
    [provider: string]: string;
  };
}

// Benchmark interface
export type Benchmark = Record<
  string,
  {
    name: string; // Display name (e.g., "S&P 500")
    type: string; // Type/category (e.g., "Market", "Factor")
    universe: string; // Shortend key (e.g., "sp500")
  }
>;

// Stock detail interface based on DynamoDB structure
export interface StockDetail {
  symbol: string; // Stock symbol (e.g., "FOX")
  name: string; // Company name (e.g., "Fox Corporation")
  logo: string; // Logo URL (e.g., "http://anp-stocks.fra1.digitaloceanspaces.com/svg/fox--big.svg")

  // Universe membership flags
  sp100: boolean; // Whether stock is in S&P 100
  sp500: boolean; // Whether stock is in S&P 500
}

export type Bucket = string;

export type IndicatorDetail = {
  id: string; // Language-independent factor id (matches strategy.factor keys)
  name: string; // Localized indicator name (e.g., "Momentum")
  description: string; // Localized description of the indicator
  bucket: Bucket; // Associated bucket/category
};

// Outlier data types
export type OutlierObservation = {
  date: string; // Date of observation (YYYY-MM-DD)
  divergent_value: number; // Actual value that's an outlier
  interval_lower_boundary: number; // Expected lower boundary
  interval_upper_boundary: number; // Expected upper boundary
};

export type OutlierProblem = {
  field: string; // Field name (e.g., "BookToMarket")
  issueType: "OUTLIER"; // Type of issue
  observations: OutlierObservation[]; // List of outlier observations
};

export type OutlierTicker = {
  ticker: string; // Stock ticker (e.g., "INTU.US")
  problems: OutlierProblem[]; // List of problems for this ticker
};

export type OutlierData = {
  [indicatorName: string]: {
    affectedTickers: OutlierTicker[];
  };
};

// Transformed outlier data indexed by ticker for easy lookup
export type OutlierDataByTicker = {
  [indicatorName: string]: {
    byTicker: Map<string, OutlierTicker>; // Map indexed by ticker symbol
    allTickers: OutlierTicker[]; // Original array for iteration
  };
};

// Stock with additional universe memberships
export interface StockWithUniverses extends StockDetail {
  // Additional universe memberships (dynamic, based on available universes)
  universes: Record<string, boolean>; // e.g., { "nasdaq100": true, "ftse100": false }
}

// Extended stock detail interface for additional data that might come from other sources
export interface ExtendedStockDetail extends StockDetail {
  sector?: string;
  industry?: string;
  marketCap?: number;
  price?: {
    current: number;
    currency: string;
    change: number;
    changePercent: number;
    lastUpdated: string;
  };
  fundamentals?: {
    pe: number;
    pb: number;
    roe: number;
    debt_to_equity: number;
    dividend_yield: number;
  };
  description?: string;
  employees?: number;
  headquarters?: string;
  founded?: string;
  website?: string;
}

// Universe detail interface (includes stocks and additional metadata)
export interface UniverseDetail extends UniverseResponse {
  stocks: (StockDetail & {
    sector?: string;
    marketCap?: number;
    weight?: number; // Weight in the universe (if applicable)
  })[];
  benchmarks: string[];
  performance?: {
    ytd: number;
    oneYear: number;
    threeYear: number;
    fiveYear: number;
  };
}

/**
 * Get all universes
 */
export function useUniverses(config?: ApiRequestConfig) {
  return useApiQuery<UniverseResponse[]>({
    path: "/universes",
    config,
    queryKey: ["universes"],
  });
}

export function useUniverseFilterData(config?: ApiRequestConfig) {
  const universeQuery = useUniverses(config);
  let filterData = undefined;
  if (universeQuery.data) {
    filterData = universeQuery.data.filter((universe) => universe.showOnFE);
  }

  return {
    ...universeQuery,
    data: filterData,
  };
}

/**
 * Get benchmarks with optional filtering
 * @param filter - Optional filter criteria (e.g., "FamaFrench" to get only Fama-French benchmarks)
 * @param config - Optional API request configuration
 */
export function useBenchmarks(filter?: string, config?: ApiRequestConfig) {
  const result = useApiQuery<Benchmark>({
    path: "/benchmarks",
    config,
    queryKey: ["benchmarks", filter],
  });

  // If filter is specified, filter the data client-side
  if (filter && result.data) {
    return {
      ...result,
      data: Object.values(result.data).filter((benchmark) => {
        return benchmark.type === filter;
      }),
    };
  }

  return result;
}

export function useFamaFrenchBenchmarks(config?: ApiRequestConfig) {
  return useBenchmarks("FamaFrench", config);
}

export function useStandardBenchmarks(config?: ApiRequestConfig) {
  return useBenchmarks("benchmarks", config);
}

/**
 * Get stock detail by symbol
 */
export function useStockDetail(tickers: string[], config?: ApiRequestConfig) {
  return useApiQuery<StockDetail[]>({
    path: `/stocks?tickers=${[...new Set(tickers)].join(",")}`,
    config,
    queryKey: ["stocks", tickers.join(",")],
    enabled: !!tickers && tickers.length > 0,
  });
}

/**
 * Get universe detail by ID (includes stocks and additional data)
 */
export function useUniverseDetail(
  universeId?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<UniverseDetail>({
    path: `/universes/${universeId}`,
    config,
    queryKey: ["universes", universeId],
    enabled: !!universeId,
  });
}

export function useBuckets(config?: ApiRequestConfig) {
  const { i18n } = useTranslation();
  const lang = i18n.language === "cs" ? "cs" : "en";

  return useApiQuery<Bucket[]>({
    path: "/buckets",
    config: {
      ...config,
      queryStringParameters: { ...config?.queryStringParameters, lang },
    },
    queryKey: ["buckets", lang],
  });
}

// The API returns a flat array of { id, name, bucket, description }; this hook
// indexes it by id (matching strategy.factor keys) so existing lookups like
// indicatorDetails?.[factorName] keep working unchanged.
export function useIndicatorDetails(config?: ApiRequestConfig) {
  const { i18n } = useTranslation();
  const lang = i18n.language === "cs" ? "cs" : "en";

  const query = useApiQuery<IndicatorDetail[]>({
    path: "/indicators",
    config: {
      ...config,
      queryStringParameters: { ...config?.queryStringParameters, lang },
    },
    queryKey: ["indicators", lang],
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    return query.data.reduce<Record<string, IndicatorDetail>>((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, {});
  }, [query.data]);

  return { ...query, data };
}

/**
 * Get stocks within a universe
 */
export function useUniverseStocks(
  universeId?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<StockDetail[]>({
    path: `/universes/${universeId}/stocks`,
    config,
    queryKey: ["universes", universeId, "stocks"],
    enabled: !!universeId,
  });
}

/**
 * Search stocks by query
 */
export function useStockSearch(query?: string, config?: ApiRequestConfig) {
  return useApiQuery<StockDetail[]>({
    path: `/stocks/search?q=${encodeURIComponent(query || "")}`,
    config,
    queryKey: ["stocks", "search", query],
    enabled: !!query && query.length > 2,
  });
}

/**
 * Get benchmark detail by ID
 */
export function useBenchmarkDetail(
  benchmarkId?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<Benchmark>({
    path: `/benchmarks/${benchmarkId}`,
    config,
    queryKey: ["benchmarks", benchmarkId],
    enabled: !!benchmarkId,
  });
}

export function useQuickFilters(config?: ApiRequestConfig) {
  return useApiQuery<QuickFilter[]>({
    path: `/quick-filters`,
    config,
    queryKey: ["quick-filters"],
  });
}

/**
 * Get data quality outliers for a specific indicator (raw data from API)
 * Returns data from S3: company-data-hub/data_quality/{universe}/{indicator}.json
 *
 * Outliers are computed per universe - the same indicator has a different
 * distribution in "sp100" than in "stoxx600" - so both parts are required.
 *
 * @param universe - The universe id (e.g., "sp100", matches Strategy.poolName)
 * @param indicator - The indicator name (e.g., "BookToMarket_Change_252")
 * @param config - Optional API request configuration
 * @internal Use useOutliers instead for transformed data with ticker lookup
 */
function useOutliersRaw(
  universe?: string,
  indicator?: string,
  config?: ApiRequestConfig
) {
  return useApiQuery<OutlierData>({
    path: `/outliers/${universe}/${indicator}`,
    config,
    queryKey: ["outliers", universe, indicator],
    enabled: !!universe && !!indicator,
    retry: (failureCount, error: any) => {
      // Don't retry on 404 - outlier file simply doesn't exist for this
      // indicator/universe combination
      if (error?.status === 404) return false;
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });
}

/**
 * Get data quality outliers for a specific indicator within a universe
 * Returns data indexed by ticker for easy lookup
 *
 * @param universe - The universe id (e.g., "sp100", matches Strategy.poolName)
 * @param indicator - The indicator name (e.g., "BookToMarket_Change_252")
 * @param config - Optional API request configuration
 * @returns Transformed data with `byTicker` Map for O(1) ticker lookup
 *
 * @example
 * const { data, isLoading } = useOutliers("sp100", "BookToMarket_Change_252");
 *
 * // Access by ticker name - O(1) lookup
 * const intuOutliers = data?.BookToMarket_Change_252?.byTicker.get("INTU.US");
 * if (intuOutliers) {
 *   console.log(intuOutliers.problems[0].observations);
 * }
 *
 * // Iterate over all tickers
 * data?.BookToMarket_Change_252?.allTickers.forEach(ticker => {
 *   console.log(ticker.ticker, ticker.problems.length);
 * });
 */
export function useOutliers(
  universe?: string,
  indicator?: string,
  config?: ApiRequestConfig
) {
  const rawQuery = useOutliersRaw(universe, indicator, config);

  // Transform the data to index by ticker and sort observations by date
  const transformedData = rawQuery.data
    ? Object.entries(rawQuery.data).reduce((acc, [indicatorName, indicatorData]) => {
        const byTicker = new Map<string, OutlierTicker>();

        // Build ticker index map and sort observations by date (most recent first)
        indicatorData.affectedTickers.forEach((ticker) => {
          // Sort observations within each problem by date descending (most recent first)
          const sortedProblems = ticker.problems.map(problem => ({
            ...problem,
            observations: [...problem.observations].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
          }));

          byTicker.set(ticker.ticker, {
            ...ticker,
            problems: sortedProblems
          });
        });

        // Also update allTickers array with sorted observations
        const sortedTickers = indicatorData.affectedTickers.map(ticker => ({
          ...ticker,
          problems: ticker.problems.map(problem => ({
            ...problem,
            observations: [...problem.observations].sort(
              (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
            )
          }))
        }));

        acc[indicatorName] = {
          byTicker,
          allTickers: sortedTickers,
        };

        return acc;
      }, {} as OutlierDataByTicker)
    : undefined;

  return {
    ...rawQuery,
    data: transformedData,
  };
}

// Example usage in a component:
/*
function UniversePage() {
  const { data: universes, isLoading: universesLoading } = useUniverses();
  const { data: benchmarks, isLoading: benchmarksLoading } = useBenchmarks();
  const { data: stockDetail } = useStockDetail("AAPL");
  const { data: universeDetail } = useUniverseDetail("sp500");

  const createMutation = useCreateUniverse();
  const updateMutation = useUpdateUniverse();
  const deleteMutation = useDeleteUniverse();

  const handleCreateUniverse = async () => {
    try {
      await createMutation.mutateAsync({
        name: "Custom Universe",
        description: "My custom stock universe",
        totalStocks: 100,
        sectors: ["Technology", "Healthcare"],
        regions: ["US", "Europe"],
      });
    } catch (error) {
      console.error('Failed to create universe:', error);
    }
  };

  const handleUpdateUniverse = async (universeId: string) => {
    try {
      await updateMutation.mutateAsync({
        universeId,
        name: "Updated Universe Name",
        description: "Updated description",
      });
    } catch (error) {
      console.error('Failed to update universe:', error);
    }
  };

  const handleDeleteUniverse = async (universeId: string) => {
    try {
      await deleteMutation.mutateAsync({ universeId });
    } catch (error) {
      console.error('Failed to delete universe:', error);
    }
  };

  if (universesLoading || benchmarksLoading) return <div>Loading...</div>;

  return (
    <div>
      <h2>Universes</h2>
      {universes?.map((universe) => (
        <div key={universe.universeId}>
          <h3>{universe.name}</h3>
          <p>{universe.description}</p>
          <p>Total Stocks: {universe.totalStocks}</p>
          <button onClick={() => handleUpdateUniverse(universe.universeId)}>Update</button>
          <button onClick={() => handleDeleteUniverse(universe.universeId)}>Delete</button>
        </div>
      ))}
      
      <h2>Benchmarks</h2>
      {benchmarks?.map((benchmark) => (
        <div key={benchmark.benchmarkId}>
          <h3>{benchmark.name} ({benchmark.symbol})</h3>
          <p>Category: {benchmark.category}</p>
          <p>YTD: {benchmark.performance.ytd}%</p>
        </div>
      ))}

      {stockDetail && (
        <div>
          <h2>Stock Detail: {stockDetail.name}</h2>
          <p>Price: {stockDetail.price.current} {stockDetail.price.currency}</p>
          <p>Change: {stockDetail.price.changePercent}%</p>
          <p>Sector: {stockDetail.sector}</p>
          <p>Market Cap: {stockDetail.marketCap}</p>
        </div>
      )}

      {universeDetail && (
        <div>
          <h2>Universe Detail: {universeDetail.name}</h2>
          <p>{universeDetail.description}</p>
          <p>Stocks: {universeDetail.stocks.length}</p>
          <ul>
            {universeDetail.stocks.map((stock) => (
              <li key={stock.symbol}>
                {stock.name} ({stock.symbol}) - {stock.sector}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button onClick={handleCreateUniverse}>Create New Universe</button>
    </div>
  );
}
*/
